import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { NumberInput } from 'foundations-components/transitional/components/NumberInput';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { Select } from 'foundations-components/transitional/components/Select';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getStaticGoal } from 'reporting-data/report/configReportGetters';
import { setStaticGoal } from 'reporting-data/report/configReportSetters';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { DateActionTypes, GOAL_EDITOR_ACTION_TYPES } from 'reporting-ui-components/constants/trackingConstants';
import { EVENTS } from 'reporting-ui-components/lib/events';
import useCloneTracker from 'reporting-ui-components/lib/useCloneTracker';
import debounce from 'transmute/debounce';
import { ADDITIONAL_OBJECT_TYPES_IN_SRV, STATIC_GOAL_EDITOR } from '../../../constants/gates';
import { useGate } from '../../../hooks/useGate';
import { ReportOptionsCategories } from '../../singleReportViewer/fullPage/sidebar/categorized/ReportOptionCategory';
import BaseSelectEditorComponent from '../common/BaseSelectEditorComponent';
import { DYANMIC_GOAL as DYNAMIC_GOAL, STATIC_GOAL } from './constants';
import useConfigChangedState from './useConfigChangedState';
import { useFetchGoalsForGoalsEditor } from './useFetchGoalsForGoalsEditor';
import { getGoalReportLayer, getGoalVisualization, getSelectedGoalTargetId, getUniqueGoalForReportId, isGoalsSupportedByReport, isStaticReferenceLineSupportedByReport } from './utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getUISelectOptionForGoal = (goalForReport, isUngatedForAdditonalObjectTypes) => {
  const {
    names,
    userAssigneeIdToNameMap,
    teamAssigneeIdToNameMap,
    milestone
  } = goalForReport;
  return {
    text: names.join(', '),
    value: getUniqueGoalForReportId(goalForReport, isUngatedForAdditonalObjectTypes),
    help: /*#__PURE__*/_jsxs("div", {
      children: [Object.keys(userAssigneeIdToNameMap).length > 0 && /*#__PURE__*/_jsx("div", {
        children: Object.values(userAssigneeIdToNameMap).join(', ')
      }), Object.keys(teamAssigneeIdToNameMap).length > 0 && /*#__PURE__*/_jsx("div", {
        children: Object.values(teamAssigneeIdToNameMap).join(', ')
      }), /*#__PURE__*/_jsx("div", {
        children: I18n.text('settingLabels.goals.milestoneTooltip', {
          milestone: I18n.text(`settingLabels.goals.milestones.${milestone.toLowerCase()}`)
        })
      })]
    })
  };
};
const GoalEditor = ({
  appUsageTracker,
  isV2FilterExperience,
  onChange,
  options,
  previewReportChanged,
  report,
  trackingApplicationName,
  trackingAssetType,
  trackingScreenName
}) => {
  const {
    gates,
    user: {
      scopes
    }
  } = useContext(UserInfoContext);
  const areAdditionalObjectTypesSupported = useGate(ADDITIONAL_OBJECT_TYPES_IN_SRV);
  const isStaticGoalEditorEnabled = useGate(STATIC_GOAL_EDITOR);
  const areStaticGoalsSupported = isStaticGoalEditorEnabled && isStaticReferenceLineSupportedByReport(report);
  const areDynamicGoalsSupported = isGoalsSupportedByReport(report, gates, scopes);
  const usageTracker = useCloneTracker(appUsageTracker);
  const {
    goals,
    loading
  } = useFetchGoalsForGoalsEditor(report, !areDynamicGoalsSupported);
  const {
    configChanged,
    setConfigChanged
  } = useConfigChangedState(report);
  const readOnly = options.readOnly;
  const staticGoal = getStaticGoal(report);

  // The selectGoalTargetId will always be undefined on mount because the goals are loaded async
  // But we can initialize it to staticGoal if possible
  const [goalType, setGoalType] = useState(typeof staticGoal === 'number' ? STATIC_GOAL : undefined);
  const selectedGoalTargetId = useMemo(() => getSelectedGoalTargetId(report, goals, areDynamicGoalsSupported, areAdditionalObjectTypesSupported), [areAdditionalObjectTypesSupported, areDynamicGoalsSupported, goals, report]);
  useEffect(() => {
    if (configChanged) {
      const newReportConfig = report.remove('visualization').remove('reportConfigs');
      onChange(newReportConfig);
      setConfigChanged(false);
    }
  }, [configChanged, onChange, report, selectedGoalTargetId, setConfigChanged]);
  const goalOptions = useMemo(() => goals.valueSeq().toArray().map(value => getUISelectOptionForGoal(value, areAdditionalObjectTypesSupported)), [goals, areAdditionalObjectTypesSupported]);
  const onDynamicGoalSelectChange = useCallback(({
    target: {
      value: newSelectedGoalId
    }
  }, isGoalReset = false) => {
    usageTracker.track(EVENTS.setSrvGoal, {
      actionType: newSelectedGoalId ? GOAL_EDITOR_ACTION_TYPES.ADD_GOAL : isGoalReset ? GOAL_EDITOR_ACTION_TYPES.RESET_GOAL : GOAL_EDITOR_ACTION_TYPES.REMOVE_GOAL,
      assetType: trackingAssetType || 'report',
      applicationName: trackingApplicationName,
      whereScreen: trackingScreenName,
      isNewFilterExperience: isV2FilterExperience
    });
    if (!newSelectedGoalId) {
      const newReportConfig = report.remove('visualization').remove('reportConfigs');
      onChange(newReportConfig);
      return;
    }
    const selectedGoal = goals.get(newSelectedGoalId);
    const secondaryReportConfig = getGoalReportLayer(selectedGoal, report, areAdditionalObjectTypesSupported);
    const newReportConfig = report.merge({
      reportConfigs: {
        secondary: secondaryReportConfig
      },
      visualization: getGoalVisualization(report, selectedGoal)
    });
    onChange(newReportConfig);
  }, [areAdditionalObjectTypesSupported, goals, isV2FilterExperience, onChange, report, trackingApplicationName, trackingAssetType, trackingScreenName, usageTracker]);
  useEffect(() => {
    if (isStaticGoalEditorEnabled && selectedGoalTargetId) {
      setGoalType(DYNAMIC_GOAL);
    }
  }, [isStaticGoalEditorEnabled, selectedGoalTargetId]);
  const removeDynamicGoal = useCallback((isGoalReset = false) => {
    onDynamicGoalSelectChange({
      target: {
        value: null
      }
    }, isGoalReset);
  }, [onDynamicGoalSelectChange]);
  const onStaticGoalChangeDebounced = useMemo(() => debounce(250, event => {
    const newGoal = event.target.value;
    const updatedReport = setStaticGoal(report, newGoal ? Number(newGoal) : undefined);
    onChange(updatedReport);
    usageTracker.track(EVENTS.changeSrvDateFilters, {
      action: DateActionTypes.STATIC_GOAL,
      assetType: trackingAssetType || 'report',
      applicationName: trackingApplicationName,
      whereScreen: trackingScreenName,
      isNewFilterExperience: isV2FilterExperience
    });
  }), [isV2FilterExperience, onChange, report, trackingApplicationName, trackingAssetType, trackingScreenName, usageTracker]);
  function removeStaticGoal() {
    onStaticGoalChangeDebounced({
      target: {
        value: ''
      }
    });
  }
  function onGoalTypeChange(e) {
    const newGoalType = e.target.value;
    setGoalType(newGoalType);
    if (newGoalType === DYNAMIC_GOAL) {
      removeStaticGoal();
    } else {
      removeDynamicGoal(false);
    }
  }
  function onStaticGoalCheckboxChange(event) {
    if (event.target.checked) {
      setGoalType(STATIC_GOAL);
    } else {
      removeStaticGoal();
      setGoalType(undefined);
    }
  }
  useEffect(() => {
    if (configChanged && selectedGoalTargetId && previewReportChanged) {
      removeDynamicGoal(true);
      setConfigChanged(false);
    }
  }, [configChanged, previewReportChanged, setConfigChanged, selectedGoalTargetId, removeDynamicGoal]);
  const label = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "settingLabels.goals.label"
  });
  if (isV2FilterExperience) {
    if (goalOptions.length === 0) {
      return /*#__PURE__*/_jsx(_Fragment, {});
    }
    return /*#__PURE__*/_jsx(BaseSelectEditorComponent, {
      tooltipHeader: label,
      clearable: true,
      options: goalOptions,
      onChange: onDynamicGoalSelectChange,
      placeholder: I18n.text('settingLabels.goals.placeholder'),
      value: selectedGoalTargetId,
      disabled: readOnly || loading
    });
  }
  const goalSelect = /*#__PURE__*/_jsx(Select, {
    "data-test-id": "goal-editor",
    buttonUse: readOnly ? 'unstyled' : 'form',
    clearable: true,
    disabled: readOnly || loading,
    options: goalOptions,
    placeholder: I18n.text('settingLabels.goals.placeholder'),
    value: selectedGoalTargetId,
    onChange: onDynamicGoalSelectChange
  });
  if (isStaticGoalEditorEnabled && areStaticGoalsSupported && areDynamicGoalsSupported) {
    return /*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-enablement.reportEditors.goalsEditor.title"
      }),
      children: /*#__PURE__*/_jsxs(ToggleGroup, {
        name: "goalType",
        children: [/*#__PURE__*/_jsx(RadioInput, {
          readOnly: readOnly,
          alignment: "center",
          value: DYNAMIC_GOAL,
          checked: goalType === DYNAMIC_GOAL,
          onChange: onGoalTypeChange,
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsxs(TFlex, {
            direction: "row",
            align: "center",
            gap: 8,
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "reporting-enablement.reportEditors.goalsEditor.goal"
            }), /*#__PURE__*/_jsx(HelpIcon, {
              size: "200",
              title: I18n.text('reporting-enablement.reportEditors.goalsEditor.goalTooltip'),
              color: "icon-core-subtle"
            }), goalSelect]
          })
        }), /*#__PURE__*/_jsx(RadioInput, {
          readOnly: readOnly,
          value: STATIC_GOAL,
          checked: goalType === STATIC_GOAL,
          onChange: onGoalTypeChange,
          alignment: "center",
          children: /*#__PURE__*/_jsxs(Flex, {
            blockSize: "40px",
            gap: "200",
            children: [/*#__PURE__*/_jsx(Box, {
              shrink: "0",
              alignSelf: "center",
              children: /*#__PURE__*/_jsx(HelpIcon, {
                size: "200",
                title: I18n.text('reporting-enablement.reportEditors.goalsEditor.staticNumberTooltip'),
                color: "icon-core-subtle",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "reporting-enablement.reportEditors.goalsEditor.staticNumber"
                })
              })
            }), goalType === STATIC_GOAL && /*#__PURE__*/_jsx(NumberInput, {
              value: staticGoal,
              onChange: onStaticGoalChangeDebounced,
              placeholder: I18n.text('reporting-enablement.reportEditors.goalsEditor.referenceLine')
            })]
          })
        })]
      })
    });
  }
  if (areStaticGoalsSupported) {
    return /*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-enablement.reportEditors.goalsEditor.title"
      }),
      children: /*#__PURE__*/_jsx(Checkbox, {
        alignment: "center",
        checked: goalType === STATIC_GOAL,
        onChange: onStaticGoalCheckboxChange,
        children: /*#__PURE__*/_jsxs(Flex, {
          blockSize: "40px",
          gap: "200",
          children: [/*#__PURE__*/_jsx(Box, {
            shrink: "0",
            alignSelf: "center",
            children: /*#__PURE__*/_jsx(HelpIcon, {
              size: "200",
              title: I18n.text('reporting-enablement.reportEditors.goalsEditor.staticNumberTooltip'),
              color: "icon-core-subtle",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "reporting-enablement.reportEditors.goalsEditor.staticNumber"
              })
            })
          }), goalType === STATIC_GOAL && /*#__PURE__*/_jsx(NumberInput, {
            value: staticGoal,
            onChange: onStaticGoalChangeDebounced,
            placeholder: I18n.text('reporting-enablement.reportEditors.goalsEditor.referenceLine')
          })]
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "settingLabels.goals.label"
    }),
    children: goalSelect
  });
};
GoalEditor.isUsableForReport = (report, gates, scopes) => {
  if (gates.includes(STATIC_GOAL_EDITOR)) {
    return isGoalsSupportedByReport(report, gates, scopes) || isStaticReferenceLineSupportedByReport(report);
  }
  return isGoalsSupportedByReport(report, gates, scopes);
};
GoalEditor.category = ReportOptionsCategories.Compare;
export default GoalEditor;